import React, { useContext, useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogActions, Button, DialogTitle, TextField, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { useModeFormStyle, useGenericDialogStyle, useScreenSizeCheck, capitalizeAll } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { useRitzTranslation } from '../../utils/customHooks';
import { LanguageSelectorContainer } from '../../containers/language-selector/LanguageSelectorContainer';



const useStyle = makeStyles(() => {
    return {
        form: {
            '& > *': {
                width: '100%',
            },
        },
    };
}, { name: 'DeviceConfigFileSettingsDialog' });


const DEFAULT_GATEWAY_TARGET = 'ug';
const DEFAULT_ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT ?? 'prod';


interface InputErrors {
    gatewayTarget?: string | undefined;
    environment?: string | undefined;
}


interface DeviceConfigFileSettingsDialogProps {
    readonly projectId: number | undefined;
    readonly open: boolean;
    readonly onCancel: ()=> void;
    readonly onSubmitDeviceConfigSettings: (gatewayTarget: string, environment: string)=> void;
}



/**
 * This is a dialog used for creating device config file
 */
export const DeviceConfigFileSettingsDialog: React.FC<DeviceConfigFileSettingsDialogProps> = (
    props: DeviceConfigFileSettingsDialogProps,
) => {

    const { state:dataState } = useContext(ritzDataContext);
    const project = props.projectId ? selectProjectById(dataState, props.projectId) : undefined;
    const { trans } = useRitzTranslation(project);

    const formClasses = useModeFormStyle();
    const dialogClasses = useGenericDialogStyle();
    const classes = useStyle();

    const isMediumScreen = useScreenSizeCheck('md');
    const [gatewayTarget, setGatewayTarget] = useState<string | undefined>(DEFAULT_GATEWAY_TARGET);
    const [environment, setEnvironment] = useState<string | undefined>(DEFAULT_ENVIRONMENT);
    const [inputErrors, setInputErrors] = useState<InputErrors>({});


    // Callback Handlers
    const onCancelHandler = props.onCancel;
    const onSubmitDeviceConfigSettings = props.onSubmitDeviceConfigSettings;


    /**
     * On Close button click, call the onCancel handler
     */
    const onCloseButtonClicked = useCallback(() => {
        onCancelHandler();
    }, [onCancelHandler]);


    /**
     * On form submit. All input are optional so we don't need to validate them.
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        const newInputErrors: InputErrors = {};

        if (gatewayTarget === undefined || !gatewayTarget.trim()) {
            newInputErrors.gatewayTarget = 'error.input.generic.empty';
        }
        if (environment === undefined || !environment.trim()) {
            newInputErrors.environment = 'error.input.generic.empty';
        }

        setInputErrors(newInputErrors);

        const hasErrors = Object.values(newInputErrors).some((error) => {
            return error !== undefined;
        });

        if (!hasErrors && gatewayTarget && environment) {
            onSubmitDeviceConfigSettings(gatewayTarget.trim(), environment.trim());
        }

        return false;
    }, [gatewayTarget, environment, onSubmitDeviceConfigSettings]);


    return (
        <Dialog
            open={props.open}
            fullScreen={!isMediumScreen}
            fullWidth={isMediumScreen}
            disableEscapeKeyDown={false}
            onClose={(event, reason) => {
                // Don't close the dialog when backdrop clicked
                if (reason !== 'backdropClick') {
                    setGatewayTarget(undefined);
                    props.onCancel();
                }
            }}
        >
            <form className={clsx(formClasses.root, classes.form)} noValidate autoComplete="on" method="POST" onSubmit={onFormSubmit}>
                <DialogTitle className={dialogClasses.dialogTitle}>
                    <div className={dialogClasses.dialogTitleText}>
                        {capitalizeAll(trans('dialog.device_config_file_settings.title'))}
                    </div>
                    <LanguageSelectorContainer projectId={props.projectId} minimal />
                </DialogTitle>

                <DialogContent
                    className={dialogClasses.dialogContent}
                >
                    <TextField
                        className={formClasses.formInputField}
                        variant="standard"
                        type="text"
                        fullWidth
                        required
                        label={capitalizeAll(trans('label.device_config_file_gateway_target'))}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            placeholder: DEFAULT_GATEWAY_TARGET,
                        }}
                        helperText={
                            inputErrors.gatewayTarget ? trans(inputErrors.gatewayTarget) : trans('label.device_config_file_gateway_target.hint')
                        }
                        error={Boolean(inputErrors.gatewayTarget)}
                        value={gatewayTarget ?? ''}
                        onChange={(event: React.FormEvent) => {
                            setGatewayTarget((event.currentTarget as HTMLInputElement).value);
                            // Clear the error for this field
                            setInputErrors((currentState) => {
                                return {
                                    ...currentState,
                                    gatewayTarget: undefined,
                                };
                            });
                        }}
                    />
                    <TextField
                        className={formClasses.formInputField}
                        variant="standard"
                        type="text"
                        fullWidth
                        required
                        label={capitalizeAll(trans('label.device_config_file_environment'))}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            placeholder: DEFAULT_ENVIRONMENT,
                        }}
                        helperText={inputErrors.environment ? trans(inputErrors.environment) : trans('label.device_config_file_environment.hint')}
                        error={Boolean(inputErrors.environment)}
                        value={environment ?? ''}
                        onChange={(event: React.FormEvent) => {
                            setEnvironment((event.currentTarget as HTMLInputElement).value);
                            // Clear the error for this field
                            setInputErrors((currentState) => {
                                return {
                                    ...currentState,
                                    environment: undefined,
                                };
                            });
                        }}
                    />
                </DialogContent>

                <DialogActions
                    className={dialogClasses.dialogActionContainer}
                >
                    <Button
                        onClick={onCloseButtonClicked}
                    >
                        {trans('label.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                    >
                        {trans('label.done')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
